import { flow, getEnv, types } from "mobx-state-tree";
import { DataGridStorageModel } from "../../components/datatable/DataGridStorage";
import { endpoints } from "../api/ApiEndpoints";
import { Column, ColumnType, DisplayValues } from "../../components/datatable/interfaces";
import dateService from "../date-service/DateService";
import { translationService } from "../translation/TranslationService";
import { ActivityTypes, getActivityLabel } from "./ActivityPricesStorage";

interface ShopActivityItems {
    items: ShopActivity[],
    totalCount: number
}

export enum FileStates {
    Success,
    Failed,
    Running,
    Deleted
}

export interface ShopActivity {
    fileId: number,
    service: ActivityTypes,
    timestamp: string,
    expiration: string,
    status: FileStates;
}

const emptyShopActivity: ShopActivity = {
    service: ActivityTypes.VideoConversion,
    timestamp: "",
    fileId: 0,
    expiration: "",
    status: FileStates.Running
}

const columns : Column[] = [
    {
        key: "service",
        name: "shop.shop-history.app",
        type: ColumnType.String
    },
    {
        key: "timestamp",
        name: "shop.shop-history.timestamp",
        type: ColumnType.DateTime
    },
    {
        key: "expiration",
        name: "shop.shop-history.expiration",
        type: ColumnType.DateTime,
    },
    {
        key: "status",
        name: "shop.shop-history.status",
        type: ColumnType.String
    },
    {
        key: "outputLink",
        name: "shop.shop-history.appLink",
        type: ColumnType.String,
        containsLink: true,
    },
]

const ShopHistoryModel = types.model({
    history: types.optional(types.frozen<ShopActivity[]>(), [emptyShopActivity])
})

export const ShopHistoryStorageModel = types
    .compose(ShopHistoryModel, DataGridStorageModel)
    .named("ShopHistoryStorageModel")
    .actions((self) => {
        return {
            deleteFiles: flow(function* (ids: number[]) {
                const endpoint = endpoints.get("mediafiles");
                yield getEnv(self).api.delete(endpoint, ids);
                self.deselectAll();
                yield self.load();
            }),
            mapResponse(response: ShopActivityItems) {
                self.history = response.items;
                self.gridColumns = columns;
                self.data = {
                    totalCount: response.totalCount,
                    items: response.items.map(self.mapItem)
                }
            },
            mapItem(item: ShopActivity) {
                var displayValues : DisplayValues = {
                    service: {value: item.service, toDisplay: translationService.translate(getActivityLabel(item.service))},
                    timestamp: {value: item.timestamp, toDisplay: dateService.formatDateTime(item.timestamp)},
                    outputLink: {
                        value: item.fileId, 
                        toDisplay: activityIsExpired(item) ? 
                            translationService.translate("shop.shop-history.expired") : translationService.translate("shop.shop-histroy.link"), 
                        path: createAppLink(item)
                    },
                    expiration: {value: item.expiration, toDisplay: dateService.formatDateTime(item.expiration)},
                    status: {value: item.status, toDisplay: translationService.translate(mapStatus(item.status))}
                }
                return {
                    displayValues: displayValues,
                    id: item.fileId
                }
            }
        }
    })
    .views((self) => ({
        get endpoint() {
            var endpoint =  endpoints.get("shop_history");
            endpoint.queryParams = self.getDataQuery();
            return endpoint;
        },
        get isLoading() {
            return !self.data;
        },
        get canLoadItems() {
            return self.history.length !== 0;
        },
        get cannotDelete() {
            if (self.selectedItems.length < 1) return true;
            return self.history.some(sa => self.selectedItems.some(id => sa.fileId.toString() === id) && activityIsExpired(sa));
        }
    }));

function mapStatus(status: FileStates) {
    switch (status) {
        case FileStates.Success: return "shop.shop-history.success";
        case FileStates.Failed: return "shop.shop-history.failed";
        case FileStates.Running: return "shop.shop-history.running";
        case FileStates.Deleted: return "shop.shop-history.deleted";
    }
}

function createAppLink(activity: ShopActivity) {
    if (activityIsExpired(activity)) return "";
    switch (activity.service) {
        case ActivityTypes.VideoConversion: return "/video-conversion?id=" + activity.fileId;
        case ActivityTypes.VideoAnalysis: return "/video-analysis?id=" + activity.fileId;
        case ActivityTypes.HighlightDetection: return "/video-segmentation?id=" + activity.fileId;
    }
}

function activityIsExpired(activity: ShopActivity) {
    return new Date(activity.expiration) < new Date() || activity.status === FileStates.Deleted
}

